/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DialogDefaultV2, InputV2 } from '../../../components/v2';
import {
  DECISAO_ITEM_HELP,
  DECISAO_ITEM_OPTIONS,
  ITEM_DECISAO_STATUSES,
  TIPO_ITEM_LABELS,
  isItemOutro,
} from './statusLabels';
import DecisaoContextoDialog from './DecisaoContextoDialog';
import ComoFuncionaDecisaoDialog from './ComoFuncionaDecisaoDialog';

const emptyValues = (item) => ({
  decisao: ITEM_DECISAO_STATUSES.includes(item?.status) ? item.status : '',
  motivoRejeicao: item?.status === 'REJEITADO' ? (item.motivoRejeicao || '') : '',
});

const toQty = (value) => {
  if (value == null || value === '') return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatQty = (value) => {
  if (value == null || value === '') return '0';
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return Number.isInteger(num) ? String(num) : String(num);
};

const locaisComSaldo = (linhas) => {
  if (!Array.isArray(linhas)) return [];
  return linhas.filter((entry) => entry?.local && toQty(entry.quantidade) > 0);
};

const formatLocais = (linhas) =>
  locaisComSaldo(linhas)
    .map((entry) => `${entry.local} (${formatQty(entry.quantidade)})`)
    .join(' · ');

const tamanhoLabel = (entry) => String(entry?.produtoTamanhoNome || '').trim();

const sameTamanho = (left, right) =>
  String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();

const tamanhoNumero = (nome) => {
  const num = Number(String(nome || '').trim().replace(',', '.'));
  return Number.isFinite(num) ? num : null;
};

const describeOutroTamanho = (entry) => {
  const locais = locaisComSaldo(entry.porLocal);
  const onde = locais
    .map((linha) => (
      locais.length === 1
        ? linha.local
        : `${linha.local} (${formatQty(linha.quantidade)})`
    ))
    .join(', ');
  const suffix = onde ? ` em ${onde}` : '';
  return `${tamanhoLabel(entry)} (${formatQty(entry.quantidadeTotal)} un.${suffix})`;
};

const sortOutrosTamanhos = (outros, pedido) => {
  const alvo = tamanhoNumero(pedido);
  if (alvo == null) return outros;
  return [...outros].sort((left, right) => {
    const distLeft = tamanhoNumero(tamanhoLabel(left));
    const distRight = tamanhoNumero(tamanhoLabel(right));
    if (distLeft == null && distRight == null) return 0;
    if (distLeft == null) return 1;
    if (distRight == null) return -1;
    return Math.abs(distLeft - alvo) - Math.abs(distRight - alvo);
  });
};

const resolveProdutoSaldo = (item, produto) => {
  const requested = toQty(item?.quantidade);
  const porTamanho = Array.isArray(produto?.porTamanho) ? produto.porTamanho : [];
  if (!porTamanho.length) {
    return {
      sized: false,
      tamanho: null,
      available: toQty(produto?.quantidadeTotal),
      requested,
      locais: locaisComSaldo(produto?.porLocal || produto?.locais),
      outros: [],
    };
  }
  const pedido = item?.tamanho || null;
  const match = porTamanho.find((entry) => sameTamanho(tamanhoLabel(entry), pedido)) || null;
  const outros = sortOutrosTamanhos(
    porTamanho.filter((entry) => (
      !sameTamanho(tamanhoLabel(entry), pedido) && toQty(entry.quantidadeTotal) > 0
    )),
    pedido
  );
  return {
    sized: true,
    tamanho: pedido,
    available: match ? toQty(match.quantidadeTotal) : 0,
    requested,
    locais: locaisComSaldo(match?.porLocal),
    outros,
  };
};

const saldoBloqueiaAprovar = (saldo) => {
  if (!saldo) return false;
  if (saldo.requested <= 0) return saldo.available <= 0;
  return saldo.available < saldo.requested;
};

const toneDoSaldo = (available, blocked) => {
  if (!blocked) return 'ok';
  return available <= 0 ? 'error' : 'warning';
};

const textoOutrosTamanhos = (saldo) => {
  if (!saldo.outros.length) {
    return saldo.available <= 0 ? 'Nenhum outro tamanho tem saldo.' : '';
  }
  const proximos = tamanhoNumero(saldo.tamanho) != null
    && saldo.outros.some((entry) => tamanhoNumero(tamanhoLabel(entry)) != null);
  if (saldo.available > 0) {
    const lista = saldo.outros.map(describeOutroTamanho).join(' · ');
    return saldo.outros.length === 1
      ? `Também há saldo no tamanho ${lista}.`
      : `Também há saldo em outros tamanhos: ${lista}.`;
  }
  if (saldo.outros.length === 1) {
    const frase = describeOutroTamanho(saldo.outros[0]);
    return proximos
      ? `O tamanho mais próximo com saldo é o ${frase}.`
      : `Há saldo em outro tamanho: ${frase}.`;
  }
  const [primeiro, ...resto] = saldo.outros;
  const principal = describeOutroTamanho(primeiro);
  const tambem = resto.map(describeOutroTamanho).join(' · ');
  if (!proximos) {
    return `Há saldo em outros tamanhos: ${saldo.outros.map(describeOutroTamanho).join(' · ')}.`;
  }
  return `O tamanho mais próximo com saldo é o ${principal}. Também há ${tambem}.`;
};

const produtoAlert = (item, produto) => {
  const saldo = resolveProdutoSaldo(item, produto);
  const blocked = saldoBloqueiaAprovar(saldo);
  const locaisTxt = formatLocais(saldo.locais);
  if (!saldo.sized) {
    if (!blocked) {
      return {
        layout: 'banner',
        tone: 'ok',
        title: `${formatQty(saldo.available)} un. no estoque`,
        body: locaisTxt || null,
        blocked,
        locais: saldo.locais,
      };
    }
    const title = saldo.available <= 0
      ? 'Sem estoque'
      : `${formatQty(saldo.available)} un. no estoque — insuficiente para ${formatQty(saldo.requested)}`;
    return {
      layout: 'banner',
      tone: toneDoSaldo(saldo.available, blocked),
      title,
      body: `O pedido é de ${formatQty(saldo.requested)} un.`,
      blocked,
      locais: saldo.locais,
    };
  }
  const qualTamanho = saldo.tamanho ? `do tamanho ${saldo.tamanho}` : 'do tamanho pedido';
  if (!blocked) {
    return {
      layout: 'banner',
      tone: 'ok',
      title: `${formatQty(saldo.available)} un. ${qualTamanho} no estoque`,
      body: locaisTxt || null,
      blocked,
      locais: saldo.locais,
    };
  }
  const title = saldo.available <= 0
    ? `Sem estoque ${qualTamanho}`
    : `${formatQty(saldo.available)} un. ${qualTamanho} — insuficiente para ${formatQty(saldo.requested)}`;
  const partes = [`O pedido é de ${formatQty(saldo.requested)} un.`];
  if (saldo.available > 0 && locaisTxt) partes.push(`${locaisTxt}.`);
  const outrosTxt = textoOutrosTamanhos(saldo);
  if (outrosTxt) partes.push(outrosTxt);
  return {
    layout: 'banner',
    tone: toneDoSaldo(saldo.available, blocked),
    title,
    body: partes.join(' '),
    blocked,
    locais: saldo.locais,
  };
};

const equipamentoAlert = (item, equipamento) => {
  const livres = equipamento?.livres != null
    ? toQty(equipamento.livres)
    : toQty(equipamento?.disponiveis);
  const requested = toQty(item?.quantidade);
  const blocked = requested <= 0 ? livres <= 0 : livres < requested;
  const comColaborador = equipamento?.comColaborador;
  const totalAtivos = equipamento?.totalAtivos != null
    ? equipamento.totalAtivos
    : equipamento?.totalUnidades;
  const extra = [
    comColaborador != null ? `${formatQty(comColaborador)} com colaborador` : null,
    totalAtivos != null ? `${formatQty(totalAtivos)} ativos` : null,
  ].filter(Boolean);
  const partes = [];
  if (blocked) partes.push(`O pedido é de ${formatQty(requested)} un.`);
  if (extra.length) partes.push(extra.join(' · '));
  const title = livres <= 0
    ? 'Sem unidades livres'
    : blocked
      ? `${formatQty(livres)} un. livres — insuficiente para ${formatQty(requested)}`
      : `${formatQty(livres)} un. livres`;
  return {
    layout: 'banner',
    tone: toneDoSaldo(livres, blocked),
    title,
    body: partes.join(' ') || null,
    blocked,
    locais: [],
  };
};

const buildLocalOptions = (linhas) => {
  const byLocal = new Map();
  locaisComSaldo(linhas).forEach((entry) => {
    byLocal.set(entry.local, (byLocal.get(entry.local) || 0) + toQty(entry.quantidade));
  });
  return Array.from(byLocal.entries()).map(([local, quantidade]) => ({
    id: local,
    nome: `${local} (${formatQty(quantidade)})`,
  }));
};

const itemRequiresLocal = (item, disponibilidade) => {
  if (!item || isItemOutro(item)) return false;
  const tipo = disponibilidade?.tipoItem || item.tipoItem;
  return tipo === 'PRODUTO';
};

const itemInputValue = (item) => {
  const nome = item?.nomeItem || '—';
  if (isItemOutro(item)) return nome;
  const tipo = TIPO_ITEM_LABELS[item?.tipoItem] || item?.tipoItem;
  return tipo ? `${nome} (${tipo})` : nome;
};

const quantidadeInputValue = (item) => {
  if (item?.quantidade == null) return '—';
  if (item.tamanho) return `${item.quantidade} (${item.tamanho})`;
  return String(item.quantidade);
};

const situacaoAlert = ({ item, disponibilidade, loading, error }) => {
  if (loading) {
    return { severity: 'info', title: 'Carregando situação…', body: null };
  }
  if (error) {
    return {
      severity: 'warning',
      title: 'Não foi possível carregar o saldo',
      body: 'Use os botões abaixo se estiverem disponíveis.',
    };
  }
  if (!disponibilidade) {
    return null;
  }
  if (disponibilidade.aplicavel === false) {
    return {
      severity: 'info',
      title: 'Situação não aplicável',
      body: disponibilidade.motivoNaoAplicavel || 'Este item não tem saldo de catálogo.',
    };
  }
  if (disponibilidade.produto) {
    return {
      severity: 'info',
      ...produtoAlert(item, disponibilidade.produto),
    };
  }
  if (disponibilidade.equipamento) {
    return {
      severity: 'info',
      ...equipamentoAlert(item, disponibilidade.equipamento),
    };
  }
  return null;
};

const catalogoAcoes = (item, disponibilidade, loading) => {
  if (loading) return [];
  if (!item?.idItem || isItemOutro(item)) return [];
  if (disponibilidade && disponibilidade.aplicavel === false) return [];
  const tipo = disponibilidade?.tipoItem || item.tipoItem;
  const idItem = disponibilidade?.idItem || item.idItem;
  if (tipo === 'PRODUTO') {
    return [
      {
        label: 'Abrir produto',
        href: `/v2/estoque/produtos?id=${encodeURIComponent(idItem)}&p=0&s=10`,
      },
    ];
  }
  if (tipo === 'EQUIPAMENTO') {
    return [
      {
        label: 'Ver equipamentos',
        href: `/v2/equipamentos?tipoEquipamento=${encodeURIComponent(idItem)}&p=0&s=10`,
      },
    ];
  }
  return [];
};

const CatalogoAcoes = ({ acoes }) => {
  if (!acoes.length) return null;
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ flexShrink: 0 }}
    >
      {acoes.map((acao) => (
        <Button
          key={acao.href}
          component="a"
          href={acao.href}
          target="_blank"
          rel="noopener noreferrer"
          size="small"
          variant="outlined"
          endIcon={<OpenInNewIcon />}
        >
          {acao.label}
        </Button>
      ))}
    </Stack>
  );
};

CatalogoAcoes.propTypes = {
  acoes: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
  })).isRequired,
};

const isStockBanner = (alert) => alert?.layout === 'banner';

const SituacaoCatalogo = ({ alert, acoes }) => {
  if (!alert && !acoes.length) return null;
  if (alert && !isStockBanner(alert)) {
    return (
      <Stack spacing={1}>
        <Alert severity={alert.severity} variant="outlined">
          <AlertTitle sx={{ mb: alert.body ? 0.5 : 0 }}>{alert.title}</AlertTitle>
          {alert.body ? alert.body : null}
        </Alert>
        <CatalogoAcoes acoes={acoes} />
      </Stack>
    );
  }
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{
        px: 1.5,
        py: 1.25,
        border: 1,
        borderColor: (
          alert?.tone === 'error' ? 'error.main'
            : alert?.tone === 'warning' ? 'warning.main'
              : 'divider'
        ),
        borderRadius: 1,
      }}
    >
      {alert ? (
        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color={
              alert.tone === 'error' ? 'error.main'
                : alert.tone === 'warning' ? 'warning.main'
                  : 'text.primary'
            }
          >
            {alert.title}
          </Typography>
          {alert.body ? (
            <Typography variant="caption" color="text.secondary">
              {alert.body}
            </Typography>
          ) : null}
        </Stack>
      ) : null}
      <CatalogoAcoes acoes={acoes} />
    </Stack>
  );
};

SituacaoCatalogo.propTypes = {
  alert: PropTypes.shape({
    severity: PropTypes.string,
    title: PropTypes.string,
    body: PropTypes.node,
    layout: PropTypes.string,
    tone: PropTypes.string,
  }),
  acoes: PropTypes.array.isRequired,
};

SituacaoCatalogo.defaultProps = {
  alert: null,
};

const DecisaoItemDialog = ({
  open,
  item,
  onClose,
  onConfirm,
  confirmLoading,
  disponibilidade,
  disponibilidadeLoading,
  disponibilidadeError,
}) => {
  const [values, setValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});
  const [pendingPayload, setPendingPayload] = useState(null);
  const [openComoFunciona, setOpenComoFunciona] = useState(false);

  useEffect(() => {
    if (!open) {
      setPendingPayload(null);
      setOpenComoFunciona(false);
      return;
    }
    setValues(emptyValues(item));
    setErrors({});
    setPendingPayload(null);
    setOpenComoFunciona(false);
  }, [open, item]);

  const isRedecisao = ITEM_DECISAO_STATUSES.includes(item?.status);
  const alert = situacaoAlert({
    item,
    disponibilidade,
    loading: disponibilidadeLoading,
    error: disponibilidadeError,
  });
  const aprovarIndisponivel = disponibilidadeLoading || !!alert?.blocked;
  const acoes = catalogoAcoes(item, disponibilidade, disponibilidadeLoading);
  const requiresLocal = pendingPayload?.decisao === 'APROVADO'
    && itemRequiresLocal(item, disponibilidade);
  const localOptions = useMemo(
    () => (requiresLocal ? buildLocalOptions(alert?.locais) : []),
    [requiresLocal, alert]
  );

  useEffect(() => {
    if (!alert?.blocked) return;
    setValues((current) => (
      current.decisao === 'APROVADO' ? { ...current, decisao: '' } : current
    ));
  }, [alert?.blocked]);

  const handleConfirm = () => {
    const nextErrors = {};
    if (!values.decisao) nextErrors.decisao = 'vazio';
    if (values.decisao === 'APROVADO' && aprovarIndisponivel) {
      nextErrors.decisao = disponibilidadeLoading ? 'loading' : 'saldo';
    }
    if (values.decisao === 'REJEITADO' && !(values.motivoRejeicao || '').trim()) {
      nextErrors.motivoRejeicao = true;
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    setPendingPayload({
      itemId: item.id,
      decisao: values.decisao,
      observacao: null,
      motivoRejeicao: values.decisao === 'REJEITADO'
        ? values.motivoRejeicao.trim()
        : null,
    });
  };

  return (
    <>
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      handlerConfirm={handleConfirm}
      title="Decisão"
      confirmMessage="Confirmar"
      cancelMessage="Voltar"
      maxWidth="md"
      fullWidth
      useFullScreen={false}
      disabledConfirmButton={confirmLoading}
      headerAction={{
        title: 'Como funciona',
        onClick: () => setOpenComoFunciona(true),
        variant: 'outlined',
      }}
    >
      <Grid container spacing={1} sx={{ mt: 0 }}>
        <Grid size={{ xs: 12, sm: 8 }}>
          <InputV2
            label="Item"
            value={itemInputValue(item)}
            disabled
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <InputV2
            label="Quantidade"
            value={quantidadeInputValue(item)}
            disabled
          />
        </Grid>
        {item?.observacao ? (
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Observação do solicitante"
              value={item.observacao}
              disabled
              multiline
              rows={2}
            />
          </Grid>
        ) : null}
        <Grid size={{ xs: 12 }}>
          <SituacaoCatalogo alert={alert} acoes={acoes} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography variant="caption" color="text.secondary">
            Ação:*
          </Typography>
          <FormControl error={!!errors.decisao} fullWidth sx={{ mt: 0.5 }}>
            <RadioGroup
              value={values.decisao}
              onChange={(event) =>
                setValues((current) => ({ ...current, decisao: event.target.value }))
              }
            >
              {DECISAO_ITEM_OPTIONS.map((option) => {
                const selected = values.decisao === option.id;
                const optionDisabled = option.id === 'APROVADO' && aprovarIndisponivel;
                const help = option.id === 'APROVADO' && alert?.blocked
                  ? 'Impossível aprovar por falta de estoque.'
                  : DECISAO_ITEM_HELP[option.id];
                return (
                  <FormControlLabel
                    key={option.id}
                    value={option.id}
                    disabled={optionDisabled}
                    control={<Radio size="small" sx={{ pt: 0.5 }} />}
                    sx={{
                      alignItems: 'flex-start',
                      ml: 0,
                      mr: 0,
                      mb: 1.5,
                      '&:last-of-type': { mb: 0 },
                    }}
                    label={(
                      <Stack spacing={0.25} sx={{ pt: 0.25 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            optionDisabled
                              ? 'text.disabled'
                              : (selected ? 'primary.main' : 'text.primary')
                          }
                        >
                          {option.nome}
                        </Typography>
                        <Typography
                          variant="body2"
                          color={optionDisabled ? 'text.disabled' : 'text.secondary'}
                        >
                          {help}
                        </Typography>
                      </Stack>
                    )}
                  />
                );
              })}
            </RadioGroup>
            {errors.decisao ? (
              <FormHelperText>
                {errors.decisao === 'saldo'
                  ? 'Impossível aprovar por falta de estoque.'
                  : errors.decisao === 'loading'
                    ? 'Aguarde o saldo do estoque'
                    : 'Selecione uma opção'}
              </FormHelperText>
            ) : null}
          </FormControl>
        </Grid>
        {values.decisao === 'REJEITADO' ? (
          <Grid size={{ xs: 12 }}>
            <InputV2
              label="Motivo da rejeição"
              isRequired
              error={!!errors.motivoRejeicao}
              value={values.motivoRejeicao}
              multiline
              rows={3}
              onChange={(event) =>
                setValues((current) => ({
                  ...current,
                  motivoRejeicao: event.target.value,
                }))
              }
            />
          </Grid>
        ) : null}
      </Grid>
    </DialogDefaultV2>
    <ComoFuncionaDecisaoDialog
      open={openComoFunciona}
      onClose={() => setOpenComoFunciona(false)}
    />
    <DecisaoContextoDialog
      open={!!pendingPayload}
      item={item}
      itemNome={item?.nomeItem}
      decisao={pendingPayload?.decisao}
      isRedecisao={isRedecisao}
      confirmLoading={confirmLoading}
      requiresLocal={requiresLocal}
      localOptions={localOptions}
      onClose={() => {
        if (confirmLoading) return;
        setPendingPayload(null);
      }}
      onConfirm={(extra) => {
        if (!pendingPayload || confirmLoading) return;
        const payload = { ...pendingPayload };
        if (requiresLocal && extra?.local) {
          payload.local = extra.local;
        } else {
          delete payload.local;
        }
        if (extra?.pedido) {
          payload.pedido = extra.pedido;
        } else {
          delete payload.pedido;
        }
        onConfirm(payload);
      }}
    />
    </>
  );
};

DecisaoItemDialog.propTypes = {
  open: PropTypes.bool,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLoading: PropTypes.bool,
  disponibilidade: PropTypes.object,
  disponibilidadeLoading: PropTypes.bool,
  disponibilidadeError: PropTypes.bool,
};

DecisaoItemDialog.defaultProps = {
  open: false,
  item: null,
  confirmLoading: false,
  disponibilidade: null,
  disponibilidadeLoading: false,
  disponibilidadeError: false,
};

export default DecisaoItemDialog;
